import React, { useRef, useEffect, useState, useCallback } from 'react';
import {
  Box,
  Typography,
  IconButton,
  Paper
} from '@mui/material';
import { styled } from '@mui/material/styles';

const ScrollBarContainer = styled(Paper)(({ theme }) => ({
  position: 'absolute',
  right: -42,
  top: 10,
  zIndex: 1000,
  padding: theme.spacing(0.3, 0.5),
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'space-between',
  backgroundColor: 'rgba(255, 255, 255, 0.98)',
  backdropFilter: 'blur(6px)',
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: theme.spacing(2),
  boxShadow: theme.shadows[2],
  maxHeight: '450px',
  minWidth: 28,
  fontSize: 'max(12px, min(20px, 1vh))',
  overflowY: 'auto',
  cursor: 'pointer',
  userSelect: 'none',
  '&::-webkit-scrollbar': {
    width: 2,
  },
  '&::-webkit-scrollbar-track': {
    backgroundColor: 'transparent',
  },
  '&::-webkit-scrollbar-thumb': {
    backgroundColor: theme.palette.primary.light,
    borderRadius: 1,
  }
}));

const AlphabetButton = styled(Box)<{ 
  isAvailable: boolean; 
  isActive: boolean; 
  magnification: number;
  disabled: boolean;
}>(({ theme, isAvailable, isActive, magnification, disabled }) => ({
  minWidth: 18,
  width: 18,
  height: 18,
  padding: '0 20px',
  margin: '0.5px 0',
  fontSize: '0.65rem',
  fontWeight: 500,
  color: disabled ? theme.palette.text.disabled : (isAvailable ? theme.palette.primary.main : theme.palette.text.secondary),
  borderRadius: theme.spacing(0.5),
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  position: 'relative',
  pointerEvents: disabled ? 'none' : 'auto',
  cursor: disabled ? 'default' : 'pointer',
  transform: `scale(${magnification})`,
  transformOrigin: '60%',
  zIndex: isActive ? 1 : 0,
  transition: theme.transitions.create(['background-color', 'transform', 'color'], {
    duration: theme.transitions.duration.short,
  }),
  '&:hover': !disabled ? {
    backgroundColor: isAvailable ? theme.palette.primary.main : theme.palette.primary.light,
    color: theme.palette.primary.contrastText,
  } : {},
  '& .letter-label': {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
  }
}));

interface AlphabeticScrollbarProps {
  contacts: Array<{
    displayName?: string;
    lastname?: string;
    companyname?: string;
    firstname?: string;
  }>;
  onLetterClick: (letter: string) => void;
  visible: boolean;
  letterMagnification?: boolean;
  magnificationMultiplier?: number;
  magnificationCurve?: number[];
  disableInvalidLetters?: boolean;
  navigateOnHover?: boolean;
  alphabet?: string;
}

const AlphabeticScrollbar: React.FC<AlphabeticScrollbarProps> = ({ 
  contacts, 
  onLetterClick, 
  visible,
  letterMagnification = true,
  magnificationMultiplier = 2,
  magnificationCurve = [1, 0.7, 0.5, 0.3, 0.1],
  disableInvalidLetters = true,
  navigateOnHover = true,
  alphabet = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'
}) => {
  const [activeLetterIndex, setActiveLetterIndex] = useState<number | null>(null);
  const [isComponentActive, setIsComponentActive] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const letters = alphabet.split('');

  // Bestimme verfügbare Buchstaben basierend auf Kontakten
  const getAvailableLetters = useCallback(() => {
    const availableSet = new Set<string>();
    contacts.forEach(contact => {
      const name = contact.displayName || contact.lastname || contact.companyname || contact.firstname;
      if (name) {
        availableSet.add(name.charAt(0).toUpperCase());
      }
    });
    return availableSet;
  }, [contacts]);

  const availableLetters = getAvailableLetters();

  // Berechne Magnifikation für einen Buchstaben
  const getMagnification = (index: number): number => {
    if (!letterMagnification || !isComponentActive || activeLetterIndex === null) {
      return 1;
    }
    
    const distance = Math.abs(activeLetterIndex - index);
    if (distance < magnificationCurve.length) {
      return magnificationCurve[distance] * (magnificationMultiplier - 1) + 1;
    }
    return 1;
  };

  // Mouse/Touch Handler
  const handleMouseMove = (event: React.MouseEvent) => {
    if (!containerRef.current || !navigateOnHover) return;
    
    const rect = containerRef.current.getBoundingClientRect();
    const y = event.clientY - rect.top;
    const letterIndex = Math.round((y / rect.height) * (letters.length - 1));
    const clampedIndex = Math.max(0, Math.min(letters.length - 1, letterIndex));
    
    setActiveLetterIndex(clampedIndex);
  };

  const handleMouseEnter = () => {
    setIsComponentActive(true);
  };

  const handleMouseLeave = () => {
    setIsComponentActive(false);
    setActiveLetterIndex(null);
  };

  const handleLetterClick = (letter: string, index: number) => {
    setActiveLetterIndex(index);
    const isAvailable = availableLetters.has(letter);
    
    if (isAvailable || !disableInvalidLetters) {
      onLetterClick(letter);
    }
  };

  if (!visible) return null;

  return (
    <ScrollBarContainer
      ref={containerRef}
      onMouseMove={handleMouseMove}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {letters.map((letter, index) => {
        const isAvailable = availableLetters.has(letter);
        const isActive = activeLetterIndex === index;
        const magnification = getMagnification(index);
        const disabled = disableInvalidLetters && !isAvailable;

        return (
          <AlphabetButton
            key={letter}
            isAvailable={isAvailable}
            isActive={isActive}
            magnification={magnification}
            disabled={disabled}
            onClick={() => handleLetterClick(letter, index)}
            title={isAvailable ? `Zu Buchstabe ${letter} springen` : `Keine Kontakte mit ${letter}`}
          >
            <span className="letter-label">{letter}</span>
          </AlphabetButton>
        );
      })}
    </ScrollBarContainer>
  );
};

export default AlphabeticScrollbar;
